import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { CustomSelectionsTemplateModelType } from 'src/api/config/customSelectionsTemplate/getCustomSelectionsTemplateList';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createCustomSelectionsTemplate';
import { apiCommon } from 'src/api/ApiCommon';
import { FormInputState, FormModel, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { validateRequire } from 'src/api/utils/formValidators';
import { CustomSelectionItemState } from './CustomSelectionItemState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';

type CustomSelectionsTemplateBodyType = ParamsType['body'];

interface CustomSelectionsTemplateLoading {
    type: 'loading';
}

interface CustomSelectionsTemplateReady {
    type: 'ready';
    value: Array<CustomSelectionsTemplateModelType>;
}

interface CustomSelectionsTemplateError {
    type: 'error';
    value: string;
}

export interface SingleFieldType {
    label: string;
    slug: string;
}

type CustomSelectionsTemplateContentResponse =
    | CustomSelectionsTemplateLoading
    | CustomSelectionsTemplateReady
    | CustomSelectionsTemplateError;

export class CustomSelectionsTemplateState {
    @observable public isShowSelectionTemplatePopup: boolean = false;
    @observable public isImageAvailable: boolean = false;
    @observable public isRatingAvailable: boolean = false;
    @observable public isColorAvailable: boolean = false;
    @observable public isDescriptionAvailable: boolean = false;
    @observable public selectedTemplate: string | null = null;
    @observable public addedFieldsList: Array<SingleFieldType> = [];
    @observable private readonly customSelectionsTemplateResource: Resource<Array<CustomSelectionsTemplateModelType>>;
    @observable public editedTemplateSlug: string | null = null;

    private readonly customSelectionItemStateMap: MobxMapAutoNew<string, CustomSelectionItemState>;

    public readonly labelModel: FormModel<string>;
    public readonly label: FormInputState<string>;
    public readonly labelStatus: InputStatus;

    public readonly fieldModel: FormModel<string>;
    public readonly field: FormInputState<string>;
    public readonly fieldStatus: InputStatus;

    constructor() {
        this.customSelectionsTemplateResource = new Resource(
            async (): Promise<Array<CustomSelectionsTemplateModelType>> => {
                return await apiCommon.getCustomSelectionsTemplateList.run({});
            }
        );

        this.customSelectionItemStateMap = new MobxMapAutoNew(
            (slug: string): CustomSelectionItemState => new CustomSelectionItemState(slug, this.getSelectionBySlug)
        );

        this.label = new FormInputState('', false);
        this.labelModel = this.label.map(validateRequire);
        this.labelStatus = new InputStatus(this.labelModel);

        this.field = new FormInputState('', false);
        this.fieldModel = this.field.map(validateRequire);
        this.fieldStatus = new InputStatus(this.fieldModel);
        makeAutoObservable(this);
    }

    getSelectionBySlug = (slug: string): CustomSelectionsTemplateModelType | null => {
        return this.customSelectionsTemplateList.find((elem) => elem.slug === slug) ?? null;
    };

    getSelectionItemStateBySlug = (slug: string): CustomSelectionItemState => {
        return this.customSelectionItemStateMap.get(slug);
    };

    @computed public get customSelectionsTemplateResponse(): CustomSelectionsTemplateContentResponse {
        const customSelections = this.customSelectionsTemplateResource.get();
        if (customSelections.type === 'ready' && customSelections.value !== null) {
            return {
                type: 'ready',
                value: customSelections.value,
            };
        }
        if (customSelections.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching event details info',
        };
    }

    @computed public get isLoading(): boolean {
        return this.customSelectionsTemplateResponse.type === 'loading';
    }

    @computed public get isReady(): boolean {
        return this.customSelectionsTemplateResponse.type === 'ready';
    }

    @computed public get errorMessage(): string | null {
        if (this.customSelectionsTemplateResponse.type === 'error') {
            return this.customSelectionsTemplateResponse.value;
        }

        return null;
    }

    @computed public get customSelectionsTemplateList(): Array<CustomSelectionsTemplateModelType> {
        if (this.customSelectionsTemplateResponse.type === 'ready') {
            return this.customSelectionsTemplateResponse.value;
        }
        return [];
    }

    @action public handleShowSelectionTemplatePopup = (): void => {
        this.isShowSelectionTemplatePopup = true;
    };

    @action public handleIsImageAvailable = (): void => {
        this.isImageAvailable = !this.isImageAvailable;
    };

    @action public handleIsRatingAvailable = (): void => {
        this.isRatingAvailable = !this.isRatingAvailable;
    };

    @action public handleIsColorAvailable = (): void => {
        this.isColorAvailable = !this.isColorAvailable;
    };

    @action public handleIsDescriptionAvailable = (): void => {
        this.isDescriptionAvailable = !this.isDescriptionAvailable;
    };

    @action public handleCloseSelectionTemplatePopup = (): void => {
        this.isShowSelectionTemplatePopup = false;
    };

    @action public createSelectionTemplate = (): void => {
        const result = this.labelModel.result;

        if (result instanceof ResultValue) {
            const data: CustomSelectionsTemplateBodyType = {
                fields: this.addedFieldsList,
                is_image: this.isImageAvailable,
                is_rating: this.isRatingAvailable,
                is_color: this.isColorAvailable,
                is_description: this.isDescriptionAvailable,
                label: result.value,
                slug: result.value.trim().toLocaleLowerCase().split(' ').join('-'),
            };
            this.handleCreateApiCall(data);
        }
    };

    @action private handleCreateApiCall = async (
        selectionTemplate: CustomSelectionsTemplateBodyType
    ): Promise<string | null> => {
        let response: string;
        try {
            response = await apiCommon.postCreateCustomSelectionsTemplate.run({ data: selectionTemplate });
            this.handleAfterRequest();
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private handleAfterRequest = (): void => {
        this.isImageAvailable = false;
        this.isRatingAvailable = false;
        this.isColorAvailable = false;
        this.isDescriptionAvailable = false;
        this.addedFieldsList = [];
        this.labelModel.reset();
        this.fieldModel.reset();
        this.customSelectionsTemplateResource.refresh();
    };

    @action public addNewField = (): void => {
        const result = this.fieldModel.result;

        if (result instanceof ResultValue) {
            const slug = result.value.trim().toLocaleLowerCase().split(' ').join('-');
            const isElemExist = this.addedFieldsList.find((elem) => elem.slug === slug) ?? null;

            if (isElemExist === null) {
                this.addedFieldsList.push({
                    slug,
                    label: result.value,
                });

                this.fieldModel.reset();
            }
        }
    };

    @action public deleteField = (slug: string): void => {
        this.addedFieldsList = this.addedFieldsList.filter((elem) => elem.slug !== slug);
    };

    @action public onSelectTemplate = (slug: string): void => {
        if (this.selectedTemplate === slug) {
            this.selectedTemplate = null;
        } else {
            this.selectedTemplate = slug;
        }
    };

    @action public handleEdit = (slug: string): void => {
        this.editedTemplateSlug = slug;
        const elem = this.getSelectionItemStateBySlug(slug);
        this.label.setValue(elem.label);
        this.isImageAvailable = elem.isImage;
        this.isRatingAvailable = elem.isRating;
        this.isColorAvailable = elem.isColor;
        this.isDescriptionAvailable = elem.isDescription;
        this.addedFieldsList = elem.fields;
    };

    @action private handleEditApiCall = async (
        selectionTemplate: CustomSelectionsTemplateBodyType,
        id: number
    ): Promise<string | null> => {
        try {
            const response = await apiCommon.putUpdateCustomSelectionsTemplate.run({ data: selectionTemplate, id });
            this.handleAfterRequest();
            return response;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public updateTemplate = async (): Promise<void> => {
        if (this.editedTemplateSlug === null) {
            return;
        }

        const elem = this.getSelectionItemStateBySlug(this.editedTemplateSlug);

        if (elem.id === null) {
            return;
        }

        const result = this.labelModel.result;

        if (result instanceof ResultValue) {
            const data: CustomSelectionsTemplateBodyType = {
                fields: this.addedFieldsList,
                is_image: this.isImageAvailable,
                is_rating: this.isRatingAvailable,
                is_color: this.isColorAvailable,
                is_description: this.isDescriptionAvailable,
                label: result.value,
                slug: result.value.trim().toLocaleLowerCase().split(' ').join('-'),
            };
            const response = await this.handleEditApiCall(data, elem.id);

            if (response !== null) {
                this.editedTemplateSlug = null;
            }
        }
    };

    @action public cancelChanges = (): void => {
        this.handleAfterRequest();
        this.editedTemplateSlug = null;
    };

    @action public handleDelete = async (id: number): Promise<void> => {
        try {
            await apiCommon.deleteCustomSelectionsTemplate.run({ id });
            this.customSelectionsTemplateResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };
}
